import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquarePlus, Send, Sparkles } from "lucide-react";
import { API, api, TOKEN_KEY } from "@/lib/api";

const EXAMPLES = ["ما حالة الكبش رقم 125؟", "ابحث عن الزبون أحمد", "كم مجموع المبالغ المقبوضة اليوم؟", "ما الكباش التي بيعت ولم تخرج من الحضيرة؟", "شحال من كبش خرج من البوابة؟", "ما الاختيارات المسجلة من الحضيرة ولم تتحول إلى مبيعات؟"];
const TOOL_LABEL = { sheep_status: "حالة الكبش", find_customer: "البحث عن الزبون", find_receipt: "البحث عن الوصل", collected: "المبالغ المقبوضة", treasury: "رصيد الخزينة",
  sold_not_exited: "الكباش غير الخارجة", exits: "الخروج من البوابة", user_activity: "عمليات المستخدم", pending_selections: "اختيارات الحضيرة", sales_summary: "ملخص المبيعات" };

export default function Assistant() {
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const end = useRef(null);
  const loadSessions = () => api.get("/assistant/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  const openSession = async (id) => { setSid(id); setMsgs((await api.get(`/assistant/sessions/${id}`)).data.map((m) => ({ role: m.role, text: m.text, tools: m.tools || [] }))); };
  const patchLast = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
  const ask = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "", tools: [] }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` }, body: JSON.stringify({ message, session_id: sid }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "تعذر الاتصال بالمساعد");
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "session") setSid(ev.session_id);
          if (ev.type === "delta") patchLast((l) => ({ ...l, text: l.text + ev.text }));
          if (ev.type === "tool") patchLast((l) => ({ ...l, tools: [...l.tools, ev.name] }));
          if (ev.type === "error") patchLast((l) => ({ ...l, text: l.text || ev.text, error: true }));
        }
      }
      loadSessions();
    } catch (e) { patchLast((l) => ({ ...l, text: e.message, error: true })); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-4 h-[calc(100vh-7rem)]" data-testid="assistant-page">
      <aside className="hidden lg:flex w-64 k-card p-3 flex-col gap-2 overflow-y-auto">
        <button className="k-btn-primary" onClick={() => { setSid(null); setMsgs([]); }} data-testid="assistant-new"><MessageSquarePlus className="w-4 h-4" /> محادثة جديدة</button>
        {sessions.map((s) => <button key={s.id} onClick={() => openSession(s.id)} className={`text-right text-sm p-2 rounded-lg truncate hover:bg-[#F6F1E7] ${sid === s.id ? "bg-[#F6F1E7] font-bold" : ""}`} data-testid="assistant-session">{s.title}</button>)}
      </aside>
      <div className="flex-1 k-card flex flex-col overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#D97706]" /><div className="flex-1"><h1 className="font-extrabold text-lg text-[#1A4D2E]" data-testid="page-title">المساعد الذكي</h1>
          <div className="text-xs text-slate-500">يجيب من بيانات التطبيق الفعلية وحسب صلاحياتك – للقراءة فقط</div></div>
          <button className="lg:hidden k-btn-outline k-btn-sm" onClick={() => { setSid(null); setMsgs([]); }}>جديدة</button></div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F6F1E7]" data-testid="assistant-messages">
          {!msgs.length && <div className="grid sm:grid-cols-2 gap-2">{EXAMPLES.map((e) => <button key={e} onClick={() => ask(e)} className="k-card p-3 text-right text-sm hover:border-[#1A4D2E]" data-testid="assistant-example">{e}</button>)}</div>}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-7 ${m.role === "user" ? "bg-[#1A4D2E] text-white" : m.error ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-white shadow-sm"}`} data-testid={`assistant-msg-${m.role}`}>
                {m.tools?.length > 0 && <div className="text-xs text-slate-500 mb-1">استعلام: {[...new Set(m.tools)].map((t) => TOOL_LABEL[t] || t).join("، ")}</div>}
                {m.text || (busy && i === msgs.length - 1 ? <Loader2 className="w-5 h-5 animate-spin" /> : "")}
              </div>
            </div>
          ))}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t flex gap-2">
          <input className="k-input flex-1" placeholder="اكتب سؤالك… مثلًا: لمن بيع الكبش 230؟" value={text} onChange={(e) => setText(e.target.value)} data-testid="assistant-input" />
          <button className="k-btn-primary px-4" disabled={busy || !text.trim()} data-testid="assistant-send">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
        </form>
      </div>
    </div>
  );
}
